/**
 * Application constants and configuration
 */

// API Configuration
export const API_CONFIG = {
  DEFAULT_TIMEOUT: 10000,
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  RATE_LIMIT_WINDOW: 60000, // 1 minute
  CACHE_TTL: {
    EXCHANGE_RATES: 5 * 60 * 1000, // 5 minutes
    HISTORICAL_DATA: 24 * 60 * 60 * 1000, // 24 hours
    CURRENCY_LIST: 7 * 24 * 60 * 60 * 1000, // 7 days
  }
}

// Currency Lists
export const MAJOR_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY'
] as const

export const POPULAR_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY',
  'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RUB', 'INR',
  'BRL', 'MXN', 'ZAR', 'KRW', 'SGD', 'HKD', 'THB', 'TRY'
] as const

export const CRYPTOCURRENCIES = [
  'BTC', 'ETH', 'LTC', 'XRP', 'ADA', 'DOT', 'LINK', 'BCH',
  'XLM', 'USDT', 'USDC', 'BNB', 'SOL', 'DOGE', 'MATIC', 'AVAX'
] as const

export const PRECIOUS_METALS = [
  'XAU', 'XAG', 'XPT', 'XPD'
] as const

// Default user preferences
export const DEFAULT_PREFERENCES = {
  baseCurrency: 'USD',
  decimalPrecision: 2,
  refreshInterval: 300000, // 5 minutes
  theme: 'dark' as const,
  language: 'en',
  region: 'US',
  numberFormat: 'standard' as const,
  showBidAsk: false,
  enableNotifications: true,
  offlineMode: false,
}

// Chart configuration
export const CHART_CONFIG = {
  DEFAULT_TIMEFRAME: '1M' as const,
  DEFAULT_CHART_TYPE: 'line' as const,
  COLORS: {
    PRIMARY: '#0ea5e9',
    SUCCESS: '#22c55e',
    DANGER: '#ef4444',
    WARNING: '#f59e0b',
    INFO: '#3b82f6',
    GRID: '#e5e7eb',
    TEXT: '#374151',
  },
  TIMEFRAMES: [
    { value: '1D', label: '1 Day' },
    { value: '1W', label: '1 Week' },
    { value: '1M', label: '1 Month' },
    { value: '3M', label: '3 Months' },
    { value: '6M', label: '6 Months' },
    { value: '1Y', label: '1 Year' },
    { value: '5Y', label: '5 Years' },
    { value: 'MAX', label: 'All Time' },
  ]
}

// Error codes and messages
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  API_RATE_LIMIT: 'API_RATE_LIMIT',
  INVALID_CURRENCY: 'INVALID_CURRENCY',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  NO_DATA_AVAILABLE: 'NO_DATA_AVAILABLE',
  CACHE_ERROR: 'CACHE_ERROR',
  STORAGE_ERROR: 'STORAGE_ERROR',
  NOTIFICATION_ERROR: 'NOTIFICATION_ERROR',
} as const

export const ERROR_MESSAGES = {
  [ERROR_CODES.NETWORK_ERROR]: 'Network connection error. Please check your internet connection.',
  [ERROR_CODES.API_RATE_LIMIT]: 'API rate limit exceeded. Please try again later.',
  [ERROR_CODES.INVALID_CURRENCY]: 'Invalid currency code provided.',
  [ERROR_CODES.INVALID_AMOUNT]: 'Please enter a valid amount greater than zero.',
  [ERROR_CODES.NO_DATA_AVAILABLE]: 'No data available for the requested currency pair.',
  [ERROR_CODES.CACHE_ERROR]: 'Error accessing cached data.',
  [ERROR_CODES.STORAGE_ERROR]: 'Error accessing local storage.',
  [ERROR_CODES.NOTIFICATION_ERROR]: 'Error sending notification.',
}

// Validation rules
export const VALIDATION_RULES = {
  MIN_AMOUNT: 0.01,
  MAX_AMOUNT: 1000000000,
  MIN_PRECISION: 0,
  MAX_PRECISION: 8,
  CURRENCY_CODE_LENGTH: 3,
  ALERT_TARGET_RATE_MIN: 0.000001,
  ALERT_TARGET_RATE_MAX: 1000000,
}

// LocalStorage keys
export const STORAGE_KEYS = {
  USER_PREFERENCES: 'currency_converter_preferences',
  WATCHLIST: 'currency_converter_watchlist',
  ALERTS: 'currency_converter_alerts',
  RECENT_CONVERSIONS: 'currency_converter_recent',
  CACHE: 'currency_converter_cache',
  THEME: 'currency_converter_theme',
} as const

// API Endpoints
export const API_ENDPOINTS = {
  EXCHANGE_RATE_API: {
    BASE_URL: 'https://api.exchangerate-api.com/v4',
    LATEST: (base: string) => `/latest/${base}`,
    PAIR: (from: string, to: string) => `/pair/${from}/${to}`,
    HISTORICAL: (base: string, date: string) => `/history/${base}/${date}`,
  },
  FIXER_IO: {
    BASE_URL: 'https://api.fixer.io',
    LATEST: '/latest',
    HISTORICAL: (date: string) => `/${date}`,
    TIMESERIES: '/timeseries',
  },
  CURRENCY_API: {
    BASE_URL: 'https://api.currencyapi.com/v3',
    LATEST: '/latest',
    HISTORICAL: '/historical',
    CURRENCIES: '/currencies',
  }
}

// Refresh intervals (in milliseconds)
export const REFRESH_INTERVALS = [
  { value: 60000, label: '1 minute' },
  { value: 300000, label: '5 minutes' },
  { value: 900000, label: '15 minutes' },
  { value: 3600000, label: '1 hour' },
  { value: 14400000, label: '4 hours' },
  { value: 86400000, label: '24 hours' },
] as const

// Notification types
export const NOTIFICATION_TYPES = {
  RATE_ALERT: 'rate_alert',
  SYSTEM_UPDATE: 'system_update',
  ERROR: 'error',
  SUCCESS: 'success',
  INFO: 'info',
} as const

// Fee structures for different transaction types
export const FEE_STRUCTURES = {
  BANK_TRANSFER: {
    percentage: 2.5,
    fixed: 5,
    description: 'Bank wire transfer fees'
  },
  CREDIT_CARD: {
    percentage: 3.0,
    description: 'Credit card foreign transaction fee'
  },
  ATM_WITHDRAWAL: {
    percentage: 1.0,
    fixed: 3,
    description: 'ATM withdrawal fees'
  },
  MONEY_EXCHANGE: {
    percentage: 4.0,
    description: 'Currency exchange service fees'
  },
  ONLINE_SERVICE: {
    percentage: 0.5,
    fixed: 1,
    description: 'Online currency exchange fees'
  }
}

// Travel expense categories
export const EXPENSE_CATEGORIES = [
  { value: 'accommodation', label: 'Accommodation', icon: '🏨' },
  { value: 'food', label: 'Food & Dining', icon: '🍽️' },
  { value: 'transport', label: 'Transportation', icon: '🚗' },
  { value: 'entertainment', label: 'Entertainment', icon: '🎭' },
  { value: 'shopping', label: 'Shopping', icon: '🛍️' },
  { value: 'other', label: 'Other', icon: '📝' },
] as const

// Business transaction types
export const BUSINESS_TRANSACTION_TYPES = [
  { value: 'invoice', label: 'Invoice', icon: '📄' },
  { value: 'payment', label: 'Payment', icon: '💳' },
  { value: 'contract', label: 'Contract', icon: '📋' },
  { value: 'expense', label: 'Expense', icon: '💸' },
] as const

// Date formats
export const DATE_FORMATS = {
  SHORT: 'MM/dd/yyyy',
  MEDIUM: 'MMM dd, yyyy',
  LONG: 'MMMM dd, yyyy',
  ISO: 'yyyy-MM-dd',
  TIMESTAMP: 'yyyy-MM-dd HH:mm:ss',
} as const

// Number formats
export const NUMBER_FORMATS = {
  STANDARD: 'standard',
  COMPACT: 'compact',
  SCIENTIFIC: 'scientific',
} as const